import { LoadingButton } from '@mui/lab'
import { Button, Stack, TextField } from '@mui/material'
import { useState } from 'react'
import BlockedHint from 'src/components/BlockedHint'
import { useBlockedReason } from 'src/components/useBlockedAction'

import type { SecondFactorAnswer } from './useLogin'

// ----------------------------------------------------------------------
// The second step of signing in with a password: the code the authenticator
// app shows, or one of the recovery codes for when the phone is not to hand.
//
// A field of its own for each, swapped rather than shared, so the browser
// offers a one-time code to the first and nothing to the second.
// ----------------------------------------------------------------------

interface SecondFactorStepProps {
  loading: boolean
  onSubmit: (answer: SecondFactorAnswer) => void
  onStartOver: () => void
}

const SecondFactorStep = ({
  loading,
  onSubmit,
  onStartOver,
}: SecondFactorStepProps) => {
  const [recovery, setRecovery] = useState(false)
  const [value, setValue] = useState('')
  const typed = value.trim()
  const blocked = useBlockedReason(!typed)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (typed) {
      onSubmit(recovery ? { recoveryCode: typed } : { code: typed })
    }
  }

  const switchTo = (toRecovery: boolean) => {
    setRecovery(toRecovery)
    setValue('')
  }

  return (
    <Stack component="form" onSubmit={submit} spacing={3}>
      {recovery ? (
        <TextField
          key="recovery"
          label="Recovery code"
          autoComplete="off"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={loading}
          fullWidth
          required
        />
      ) : (
        <TextField
          key="code"
          label="Code"
          autoComplete="one-time-code"
          inputProps={{ inputMode: 'numeric', maxLength: 7 }}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={loading}
          fullWidth
          required
        />
      )}
      <BlockedHint
        reason={
          blocked.blocked
            ? recovery
              ? 'Enter a recovery code'
              : 'Enter the code'
            : null
        }
        reasonProps={blocked.reasonProps}
        sx={{ width: '100%' }}
      >
        <LoadingButton
          type="submit"
          variant="contained"
          size="large"
          loading={loading}
          aria-disabled={blocked.blocked}
          disabled={blocked.blocked}
          fullWidth
        >
          Continue
        </LoadingButton>
      </BlockedHint>
      <Stack spacing={1} alignItems="center">
        <Button
          variant="text"
          onClick={() => switchTo(!recovery)}
          disabled={loading}
        >
          {recovery
            ? 'Use the code from your app instead'
            : 'Use a recovery code instead'}
        </Button>
        <Button
          variant="text"
          color="inherit"
          onClick={onStartOver}
          disabled={loading}
        >
          Start again
        </Button>
      </Stack>
    </Stack>
  )
}

export default SecondFactorStep
